import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "@shared/hooks";
import { actions as sharedActions } from "@shared/store";
import { OVERLAY_TYPES } from "@shared/constants";
import { OverlaySize } from "@shared/interfaces";
import { SuggestedCollaborator } from "@shared/models";
import { getFullName } from "@shared/utils";
import { actions as SparksActions, selectors as SparksSelectors } from "@containers/Recognition/store/sparks";

// Relative, not the "@containers/Recognition/components" alias: jest resolves no path aliases, and
// this component's own suite needs the REAL CardHead/LeaderboardLoadStatus rendering rather than
// stubbed markers -- same reasoning as OverviewLeaderboard.tsx's own relative imports of its Leaderboard-
// folder siblings, and BadgesSection's own relative import of CardHead.
import { CardHead } from "../../CardHead";
import { LeaderboardLoadStatus } from "../LeaderboardLoadStatus";
import { CollaboratorCard } from "../CollaboratorCard";
import { SPARK_SPREAD_EMPTY_STATE_MESSAGE, SPARK_SPREAD_LOAD_FAILED_MESSAGE } from "../leaderboardEmptyCopy";
import "./index.scss";

// Up to four cards, never padded (923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §3: "rendering FEWER than four --
// including zero -- is CORRECT"). The api itself never returns more than four, but slicing here keeps
// that a property of this component too, rather than an unstated assumption about the response.
const MAX_CARDS = 4;

/**
 * "Spread your Sparks" (923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §3/§4 S3): a full-width section below the
 * Leaderboard tab's board grid, listing up to four teammates the viewer has worked closely with
 * recently and hasn't yet recognised this quarter. Self-contained, mirroring OverviewLeaderboard's own
 * shape: its own complete data lifecycle (own action triad, own `takeLatest` saga slot, own `useLoader`
 * name "LeaderboardCollaborators", own slice fields reset by `clearSparks`), rendered via the
 * `@containers/Recognition/components` alias and wrapped in its OWN `ErrorBoundary` at the CALL SITE
 * (`LeaderboardsContainer.tsx`), matching that file's own per-region convention for the main column and
 * the rail -- not a second, internal boundary here.
 *
 * The fetch takes no query params at all (§4 S2: "the feature should remind you to recognize people you
 * work with closely, not anything more complicated" -- no board/period), so the effect below depends on
 * `[dispatch]` only, unlike every other fetch in this container that re-fires on a board/period toggle.
 *
 * Clicking a card's Recognise button prefills the existing "New Recognition" form instead of opening it
 * blank -- `openRecognitionFormOverlay`'s shape, widened with an optional `recipient` (formHelpers.tsx's
 * own `getInitValues` comment), built from the row with no extra fetch.
 */
const SparkSpreadSection: FC = () => {
  const dispatch = useDispatch();

  const collaborators = useSelector(SparksSelectors.getLeaderboardCollaborators());
  const isLoaded = useSelector(SparksSelectors.getLeaderboardCollaboratorsIsLoaded());
  const loadFailed = useSelector(SparksSelectors.getLeaderboardCollaboratorsLoadFailed());

  const { isLoading } = useLoader({
    name: "LeaderboardCollaborators",
    actionTypes: [SparksActions.getLeaderboardCollaborators],
  });

  // Period-independent, so this depends on [dispatch] only -- NOT board/period like the tab's own two
  // fetches (getLeaderboard/getLeaderboardCrowns effects in LeaderboardsContainer.tsx).
  useEffect(() => {
    dispatch(SparksActions.getLeaderboardCollaborators.request());
  }, [dispatch]);

  const handleRecognise = (collaborator: SuggestedCollaborator) => {
    dispatch(
      sharedActions.showOverlay({
        type: OVERLAY_TYPES.CREATE_RECOGNITION,
        // 923-core-context context/engineering/spark-rewards/phase-54-give-recognition-drawer.md: the redesigned drawer, not the old centred modal.
        size: OverlaySize.DRAWER,
        closeOnOverlayClick: true,
        props: {
          recipient: { label: getFullName(collaborator.user), value: String(collaborator.user_id) },
        },
      }),
    );
  };

  const cards = (collaborators ?? []).slice(0, MAX_CARDS);
  // Settled, not loading, not failed, and genuinely zero -- same shape as every other empty-state guard
  // in this folder (LeaderboardsContainer.tsx's showResultsEmptyState, OverviewLeaderboard's showEmpty).
  // `isLoaded` (flips true only on .success) is what keeps this from painting on the very first frame,
  // before the mount effect's own fetch has even been observed as loading.
  const showEmptyState = isLoaded && !isLoading && !loadFailed && cards.length === 0;

  return (
    <div className="spark-spread-section">
      <CardHead title="Spread your Sparks" className="spark-spread-section-head" />
      <div className="spark-spread-section-subcopy">
        {"Teammates you haven't recognised this quarter. Only you see this list."}
      </div>
      {/* browser-verify fix: this section's OWN failure copy, not LeaderboardLoadStatus's exported
          LEADERBOARD_LOAD_FAILED_MESSAGE -- that constant names "the leaderboard", which is actively
          misleading when only this section's fetch fails while the ranked board above it is visibly
          fine. See SPARK_SPREAD_LOAD_FAILED_MESSAGE's own comment in leaderboardEmptyCopy.ts. */}
      <LeaderboardLoadStatus
        isLoading={isLoading}
        loadFailed={loadFailed}
        failureTestId="spark-spread-load-failed"
        failureMessage={SPARK_SPREAD_LOAD_FAILED_MESSAGE}
      />
      {showEmptyState ? (
        <div className="spark-spread-section-empty" data-testid="spark-spread-empty">
          {SPARK_SPREAD_EMPTY_STATE_MESSAGE}
        </div>
      ) : null}
      {/* No `showEmptyState`/`!loadFailed` guard here -- `.failure` never clears leaderboardCollaborators
          (house rule), so a refetch that fails leaves already-rendered cards on screen alongside the
          failure banner above, rather than clearing them (923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3 test 5). */}
      {cards.length > 0 ? (
        <div className="spark-spread-section-cards">
          {cards.map((collaborator) => (
            <CollaboratorCard key={collaborator.user_id} collaborator={collaborator} onRecognise={handleRecognise} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SparkSpreadSection;
